import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../../components/headers/Header';
import { runHolidayCorrection } from '../../services/leavesAPIs';

const HolidayCorrection = () => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [correctLeaves, setCorrectLeaves] = useState(true);
  const [correctAbsences, setCorrectAbsences] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');

  const handleAddDate = () => {
    if (!currentDate) return;
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    if (!selectedDates.includes(dateStr)) {
      setSelectedDates([...selectedDates, dateStr]);
    }
    setCurrentDate(null);
  };

  const handleRemoveDate = (dateStr) => {
    setSelectedDates(selectedDates.filter((d) => d !== dateStr));
  };

  const handleSubmitClick = () => {
    if (selectedDates.length === 0) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const res = await runHolidayCorrection(selectedDates, correctLeaves, correctAbsences);
      setResult(res);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to apply holiday correction');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedDates([]);
    setCurrentDate(null);
    setCorrectLeaves(true);
    setCorrectAbsences(true);
    setResult(null);
    setError('');
  };

  return (
    <Box>
      <PageHeader
        title="Holiday Correction"
        subtitle="Correct leave balances and absences for public holidays"
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Correction applied successfully — {result.leaves.length} leave
            request{result.leaves.length !== 1 ? 's' : ''} updated,{' '}
            {result.absences.length} absence{result.absences.length !== 1 ? 's' : ''} affected.
          </Alert>
        )}

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Select Holiday Dates
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <DatePicker
              label="Pick a date"
              value={currentDate}
              onChange={setCurrentDate}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 200 }
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddDate}
              disabled={!currentDate}
            >
              Add Date
            </Button>
          </Box>
        </LocalizationProvider>

        {selectedDates.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {selectedDates.map((dateStr) => (
              <Chip
                key={dateStr}
                label={dateStr}
                onDelete={() => handleRemoveDate(dateStr)}
                deleteIcon={<DeleteIcon />}
                variant="outlined"
              />
            ))}
          </Box>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={correctLeaves}
              onChange={(e) => setCorrectLeaves(e.target.checked)}
            />
          }
          label="Correct leave balances"
          sx={{ mb: 1 }}
        />
        <br />
        <FormControlLabel
          control={
            <Checkbox
              checked={correctAbsences}
              onChange={(e) => setCorrectAbsences(e.target.checked)}
            />
          }
          label="Remove holiday absences"
          sx={{ mb: 3 }}
        />
        <br />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmitClick}
            disabled={selectedDates.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? 'Applying...' : 'Apply Holiday Correction'}
          </Button>
          {result && (
            <Button variant="outlined" onClick={handleReset}>
              Clear & Start Over
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Holiday Correction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to apply a holiday correction for{' '}
            <strong>{selectedDates.length}</strong> date
            {selectedDates.length !== 1 ? 's' : ''}:
          </DialogContentText>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {selectedDates.map((d) => (
              <Chip key={d} label={d} size="small" variant="outlined" />
            ))}
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            <strong>Options:</strong>
            <br />
            {correctLeaves ? '✓ Correct leave balances' : '✗ Skip leave balance correction'}
            <br />
            {correctAbsences ? '✓ Remove holiday absences' : '✗ Skip absence removal'}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            This action changes data and cannot be undone automatically.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm}>
            Confirm & Apply
          </Button>
        </DialogActions>
      </Dialog>

      {result && (
        <>
          {result.leaves.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Corrected Leave Requests ({result.leaves.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Leave Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Days Returned</TableCell>
                      <TableCell>Excluded Dates</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.leaves.map((leave) => (
                      <TableRow key={leave.requestId}>
                        <TableCell>
                          {leave.employeeName || leave.employeeId}
                        </TableCell>
                        <TableCell>{leave.leaveType}</TableCell>
                        <TableCell>{leave.status}</TableCell>
                        <TableCell>{leave.daysReturned ?? '-'}</TableCell>
                        <TableCell>
                          {leave.excludedDates?.join(', ') || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {result.absences.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Affected Absences ({result.absences.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Date Range</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.absences.map((abs) => (
                      <TableRow key={abs.absenceId}>
                        <TableCell>
                          {abs.employeeName || abs.employeeId}
                        </TableCell>
                        <TableCell>{abs.type}</TableCell>
                        <TableCell>{abs.category}</TableCell>
                        <TableCell>
                          <Chip
                            label={abs.action}
                            size="small"
                            color={abs.action === 'deleted' ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {abs.date} — {abs.endDate || abs.date}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default HolidayCorrection;
